import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { auth } from '@/api/endpoints';
import { AUTH_REQUIRED_EVENT } from '@/api/client';

/**
 * AuthProvider
 * ============
 *
 * Resolves the current session via `/api/medical/whoami/`, exposes a
 * minimal auth context, and reacts to 401 events from the API client by
 * redirecting to the Django-rendered login page.
 *
 * We intentionally do NOT manage tokens — Django handles login. The SPA
 * just asks "who am I" and re-checks when the user comes back from a tab
 * that may have lost session.
 */

export type WhoAmI = {
  is_authenticated: boolean;
  user_id: number | null;
  email: string;
  user_type: 'guest' | 'employee' | 'owner' | 'superuser' | null;
  has_patient_profile: boolean;
  has_clinician_profile: boolean;
  can_administer_practice: boolean;
  permissions: string[];
};

type AuthContextValue = {
  status: 'loading' | 'authenticated' | 'anonymous';
  user: WhoAmI | null;
  refresh: () => Promise<void>;
  redirectToLogin: (next?: string) => void;
  hasPerm: (codename: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const ANONYMOUS: WhoAmI = {
  is_authenticated: false,
  user_id: null,
  email: '',
  user_type: null,
  has_patient_profile: false,
  has_clinician_profile: false,
  can_administer_practice: false,
  permissions: [],
};

const LOGIN_URL = '/auth/login/';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<WhoAmI | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  const refresh = useCallback(async () => {
    try {
      const me = await auth.whoami();
      setUser(me);
      setStatus(me.is_authenticated ? 'authenticated' : 'anonymous');
    } catch {
      setUser(ANONYMOUS);
      setStatus('anonymous');
    }
  }, []);

  const redirectToLogin = useCallback((next?: string) => {
    const target = next ?? window.location.pathname + window.location.search;
    const url = `${LOGIN_URL}?next=${encodeURIComponent(target)}`;
    window.location.assign(url);
  }, []);

  const hasPerm = useCallback(
    (codename: string) => Boolean(user?.permissions?.includes(codename)),
    [user],
  );

  // Initial whoami
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // React to 401s coming from anywhere
  useEffect(() => {
    const handler = () => {
      setStatus('anonymous');
      setUser(ANONYMOUS);
    };
    window.addEventListener(AUTH_REQUIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, handler);
  }, []);

  // Refresh whoami when the tab regains focus (covers session expiry mid-day)
  useEffect(() => {
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, refresh, redirectToLogin, hasPerm }),
    [status, user, refresh, redirectToLogin, hasPerm],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
